import { DataResult } from '@progress/kendo-data-query';
import { Button, ButtonGroup, ButtonProps } from '@progress/kendo-react-buttons';
import { getter } from '@progress/kendo-react-common';
import {
    getSelectedState,
    Grid as KendoReactGrid,
    GridHeaderSelectionChangeEvent,
    GridItemChangeEvent,
    GridProps,
    GridSelectableMode,
    GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { Tooltip } from '@progress/kendo-react-tooltip';
import cn from 'classnames';
import _ from 'lodash';
import { Children, FC, isValidElement, ReactNode, useCallback, useEffect, useMemo, useState, VFC } from 'react';
import { GridColumn, GridColumnProps } from '.';
import { IActionButtons, IDialogResult, useConfirmation } from '../../dialogs';
import { AddItemProvider } from './AddItemContext';
import { GridColumnFieldsProvider } from './GridColumnFieldsContext';
import { SelectedDataProvider } from './SelectedDataContext';

const DATA_ITEM_KEY = 'id';
export const SELECTED_FIELD = 'selected';
export const EDIT_FIELD = 'inEdit';
export const HAS_ACCSS_TO_EDIT_FIELD = 'hasAccessToEdit';

type ItemChangedResult = Promise<void | boolean> | (void | boolean);

type Props = Omit<GridProps, 'selectable'> & {
    noHeader?: boolean;
    selectable?: boolean;
    selectableMode?: GridSelectableMode;
    onSelectedChange?: (selectedDataItems: any[]) => void;
    removeable?: boolean;
    removeField?: string;
    removeableWithoutConfirmation?: boolean;
    /**
     * @default Pašalinti
     */
    removeableTooltip?: string;
    removeDialogTitle?: string;
    removeDialogComponent?: VFC<{ dataItem }>;
    removeButtonProps?: ButtonProps;
    editable?: boolean;
    isLoading?: boolean;
    onItemEdit?: (dataItem) => ItemChangedResult;
    onItemAdd?: (dataItem) => ItemChangedResult;
    onItemRemove?: (dataItem) => ItemChangedResult;
    onItemExitEdit?: (dataItem) => Promise<void> | void;
    hideColumns?: boolean;
    columnsToKeepVisible?: string[];
    dataItemKey?: string;
};

export const Grid: FC<Props> = ({ children, noHeader, className, onSelectedChange, ...props }) => {
    const {
        data: data1,
        selectable,
        selectedField,
        useSelectedDataContextProvider,
        SelectedColumn,
        onHeaderSelectionChange,
        onSelectionChange,
        selectedData
    } = useSelectable(props.selectable, props.selectableMode, props.selectedField, onSelectedChange, props.data, props.dataItemKey);

    const { RemoveColumn } = useRemoveable(props);

    const { editField, EditColumn, data: data2, AddItemContextProvider, onItemChange } = useEditable(props, data1);

    const { data, rowRender } = useLoading(props, data2);

    const { children: _children, GridColumnFieldsContextProvider } = useHiddenColumns(props, children);

    const { children: _children2 } = useMinWidth([SelectedColumn, _children, RemoveColumn, EditColumn]);

    let grid = (
        <KendoReactGrid
            {...props}
            className={noHeader ? `k-grid-no-header ${className}` : className}
            data={data}
            selectable={selectable}
            selectedField={selectedField}
            onHeaderSelectionChange={onHeaderSelectionChange}
            onSelectionChange={onSelectionChange}
            editField={editField}
            onItemChange={onItemChange}
            rowRender={rowRender}
        >
            {_children2}
        </KendoReactGrid>
    );

    if (useSelectedDataContextProvider) {
        grid = <SelectedDataProvider value={{ selectedData }}>{grid}</SelectedDataProvider>;
    }

    if (AddItemContextProvider) {
        grid = <AddItemContextProvider>{grid}</AddItemContextProvider>;
    }

    if (GridColumnFieldsContextProvider) {
        grid = <GridColumnFieldsContextProvider>{grid}</GridColumnFieldsContextProvider>;
    }

    return grid;
};

Grid.defaultProps = { removeableTooltip: 'Pašalinti' };

const useMinWidth = (children: ReactNode) => {
    const [currentWidth, setCurrentWidth] = useState<number>();

    const handleResize = () => setCurrentWidth(document.querySelector('.k-grid>.k-grid-container>.k-grid-content')?.clientWidth || 0);

    const debouncedHandleResize = useMemo(() => _.throttle(handleResize, 200), []);

    const minWidth = useMemo(
        () =>
            Children.toArray(children)
                .filter(isValidElement)
                .reduce((prev, cur) => prev + ((cur.props as GridColumnProps).minWidth || 0), 0),
        [children]
    );

    useEffect(() => {
        debouncedHandleResize();

        window.addEventListener('resize', debouncedHandleResize);

        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    }, []);

    const reservedWidth = useMemo(
        () =>
            Children.toArray(children)
                .filter(isValidElement)
                .reduce((prev, cur) => prev + (((cur.props as GridColumnProps).width as number) || 0), 0),
        [children]
    );

    const getWidth = useCallback(
        (width: number, targetWidth: number) => {
            if (width !== undefined && width !== null) {
                return width;
            }

            if (targetWidth === undefined || targetWidth === null) {
                return targetWidth;
            }

            const availableWidth = currentWidth - reservedWidth;

            if (minWidth >= availableWidth) {
                return targetWidth;
            }

            return (targetWidth / minWidth) * availableWidth;
        },
        [minWidth, reservedWidth, currentWidth]
    );

    const childrenToReturn = useMemo(
        () =>
            Children.toArray(children)
                .filter(isValidElement)
                .map(e => {
                    const props = e.props as GridColumnProps;

                    return {
                        ...e,
                        props: {
                            ...props,
                            width: getWidth(props.width as number, props.minWidth),
                            locked:
                                props.locked !== undefined
                                    ? props.locked
                                    : props.lockedWhenHorizontalBarIsVisible && currentWidth < minWidth + reservedWidth
                        }
                    };
                }),
        [children, getWidth]
    );

    return {
        children: childrenToReturn
    };
};

const useHiddenColumns = ({ hideColumns, columnsToKeepVisible }: Props, children: ReactNode) => {
    if (!hideColumns) {
        return { children, GridColumnFieldsContextProvider: null };
    }

    return {
        children: Children.toArray(children)
            .filter(isValidElement)
            .filter(
                e =>
                    e.type['displayName'] !== GridColumn.displayName ||
                    !(e.props as GridColumnProps).canBeHidden ||
                    columnsToKeepVisible.includes((e.props as GridColumnProps).field)
            ),
        GridColumnFieldsContextProvider: ({ children: providerChildren }) => (
            <GridColumnFieldsProvider gridChildren={children}>{providerChildren}</GridColumnFieldsProvider>
        )
    };
};

const useSelectable = (
    selectable: boolean,
    selectableMode: GridSelectableMode,
    selectedField: string,
    onSelectedChange: (selectedDataItems: any[]) => void,
    data: any[] | DataResult,
    dataItemKey: string
) => {
    const idGetter = getter(dataItemKey || DATA_ITEM_KEY);
    const [selectedState, setSelectedState] = useState({});
    useEffect(() => setSelectedState({}), [selectable]);

    const getData = useCallback((_data: any[]) => _data.map(e => ({ ...e, [SELECTED_FIELD]: selectedState[idGetter(e)] ?? false })), [selectedState]);
    const data2 = useMemo(() => (Array.isArray(data) ? getData(data) : { data: getData(data?.data ?? []), total: data?.total ?? 0 }), [
        data,
        getData
    ]);
    const getSelectedData = useCallback((_data: any[]) => _data.filter(e => selectedState[idGetter(e)]), [selectedState]);
    const selectedData = useMemo(() => getSelectedData((Array.isArray(data) ? data : data?.data) ?? []), [data, getSelectedData]);

    useEffect(() => {
        onSelectedChange?.(selectedData);
    }, [selectedData]);

    if (selectable !== true) {
        return { selectable: undefined, selectedField, data };
    }

    return {
        selectable: { enabled: true, drag: true, cell: false, mode: selectableMode ?? 'multiple' },
        selectedField: SELECTED_FIELD,
        data: data2,
        selectedData,
        useSelectedDataContextProvider: true,
        SelectedColumn: (
            <GridColumn
                field={SELECTED_FIELD}
                width={42}
                headerSelectionValue={((Array.isArray(data) ? data : data?.data) ?? []).every(e => selectedState[idGetter(e)])}
                lockedWhenHorizontalBarIsVisible
            />
        ),
        onSelectionChange: (event: GridSelectionChangeEvent) => {
            const newSelectedState = getSelectedState({
                event: { ...event, ctrlKey: selectableMode === 'single' ? event.ctrlKey : event.shiftKey ? event.ctrlKey : !event.ctrlKey },
                selectedState,
                dataItemKey: dataItemKey || DATA_ITEM_KEY
            });
            setSelectedState(newSelectedState);
        },
        onHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => {
            const checked = event.syntheticEvent.target['checked'];
            const newSelectedState = {};

            if (selectableMode === 'single' && checked) {
                event.dataItems.forEach(e => (newSelectedState[idGetter(e)] = false));
            } else {
                event.dataItems.forEach(e => (newSelectedState[idGetter(e)] = checked));
            }

            setSelectedState(newSelectedState);
        }
    };
};

const handleItemRemove = async (
    dataItem,
    removeableWithoutConfirmation: boolean,
    confirmAsync: (title: string, message: string | ReactNode, actionButtons?: IActionButtons) => Promise<IDialogResult>,
    title: string,
    message: string | ReactNode,
    onItemRemove: (dataItem: any) => ItemChangedResult
) => {
    if (removeableWithoutConfirmation === true || (await confirmAsync(title, message)).success) {
        onItemRemove?.(dataItem);
    }
};

const useRemoveable = ({
    removeable,
    removeField,
    removeableWithoutConfirmation,
    removeableTooltip,
    removeDialogTitle,
    removeDialogComponent: RemoveDialogComponent,
    removeButtonProps,
    onItemRemove
}: Props) => {
    const { confirmAsync } = useConfirmation();

    if (!removeable) {
        return {};
    }

    return {
        RemoveColumn: (
            <GridColumn
                width={38}
                lockedWhenHorizontalBarIsVisible
                cell={({ dataItem, className, style }) => (
                    <td className={cn('p-0 px-1', className)} style={style}>
                        {!!removeField && !dataItem[removeField] ? null : (
                            <Tooltip parentTitle anchorElement="target" position="left" openDelay={400}>
                                <Button
                                    type="button"
                                    className="k-error"
                                    look="flat"
                                    iconClass="far fa-fw fa-trash"
                                    title={removeableTooltip}
                                    onClick={async () =>
                                        handleItemRemove(
                                            dataItem,
                                            removeableWithoutConfirmation,
                                            confirmAsync,
                                            removeDialogTitle ?? removeableTooltip,
                                            RemoveDialogComponent !== undefined ? (
                                                <RemoveDialogComponent dataItem={dataItem} />
                                            ) : (
                                                `Ar tikrai norite ${removeableTooltip.toLocaleLowerCase()} pasirinktą įrašą?`
                                            ),
                                            onItemRemove
                                        )
                                    }
                                    {...(removeButtonProps ?? {})}
                                />
                            </Tooltip>
                        )}
                    </td>
                )}
            />
        )
    };
};

const useEditable = (
    {
        editable,
        removeable,
        removeableWithoutConfirmation,
        removeableTooltip,
        removeDialogTitle,
        removeDialogComponent: RemoveDialogComponent,
        editField,
        dataItemKey,
        onItemChange,
        onItemEdit,
        onItemAdd,
        onItemRemove,
        onItemExitEdit
    }: Props,
    data: any[] | DataResult
) => {
    const idGetter = getter(dataItemKey || DATA_ITEM_KEY);
    const [editState, setEditState] = useState({});

    const newItemEditState = useMemo(() => editState[0] ?? editState['undefined'] ?? editState['null'], [editState]);
    const newItem = useMemo(() => (newItemEditState?.[EDIT_FIELD] === true ? newItemEditState : undefined), [newItemEditState]);

    const AddItemContextProvider = useCallback(
        ({ children }) => (
            <AddItemProvider
                value={{
                    canAdd: !newItem,
                    addItem: item =>
                        setEditState(cur => ({
                            ...cur,
                            [idGetter(item)]: { ...item, [EDIT_FIELD]: true }
                        }))
                }}
            >
                {children}
            </AddItemProvider>
        ),
        [newItem]
    );

    const { confirmAsync } = useConfirmation();

    if (!editable) {
        return { editField, data, onItemChange };
    }

    const enterEdit = dataItem => {
        const newEditState = editState;
        editState[idGetter(dataItem)] = { [EDIT_FIELD]: true };
        setEditState({ ...newEditState });
    };

    const exitEdit = async dataItem => {
        const newEditState = editState;
        editState[idGetter(dataItem)] = { [EDIT_FIELD]: false };
        setEditState({ ...newEditState });

        await onItemExitEdit?.(dataItem);
    };

    const onSaveEdit = async dataItem => {
        const itemState = editState[idGetter(dataItem)];
        const item = { ...dataItem, ...itemState };
        return onItemEdit?.(item);
    };

    const getData = (_data: any[]) => [
        ...(newItem ? [newItem] : []),
        ..._data.map(e => ({ ...e, ...(editState[idGetter(e)] ?? { [EDIT_FIELD]: false }) }))
    ];

    return {
        editField: EDIT_FIELD,
        data: Array.isArray(data) ? getData(data ?? []) : { data: getData(data?.data ?? []), total: data?.total ?? 0 },
        onItemChange: (event: GridItemChangeEvent) => {
            const newEditState = editState;
            editState[idGetter(event.dataItem)][event.field] = event.value;
            setEditState({ ...newEditState });
        },
        AddItemContextProvider,
        EditColumn: (
            <GridColumn
                width={68}
                lockedWhenHorizontalBarIsVisible
                cell={({ dataItem, className, style }) => {
                    const inEdit = dataItem[EDIT_FIELD];
                    const hasAccessToEdit = dataItem[HAS_ACCSS_TO_EDIT_FIELD] ?? true;
                    const isNew = !idGetter(dataItem);
                    return (
                        <td className={cn('p-0 px-1', className)} style={style}>
                            {hasAccessToEdit && (
                                <Tooltip parentTitle anchorElement="target" position="left" openDelay={400}>
                                    {isNew ? (
                                        <NewItemButtons
                                            onSave={async () => {
                                                const result = await onItemAdd?.(dataItem);
                                                if (result !== false) {
                                                    await exitEdit(dataItem);
                                                }
                                            }}
                                            onCancel={async () => exitEdit(dataItem)}
                                        />
                                    ) : inEdit ? (
                                        <ItemInEditButtons
                                            onSave={async () => {
                                                const result = await onSaveEdit(dataItem);
                                                if (result !== false) {
                                                    await exitEdit(dataItem);
                                                }
                                            }}
                                            onCancel={async () => exitEdit(dataItem)}
                                        />
                                    ) : (
                                        <EditButtons
                                            onEdit={() => enterEdit(dataItem)}
                                            onRemove={async () =>
                                                handleItemRemove(
                                                    dataItem,
                                                    removeableWithoutConfirmation,
                                                    confirmAsync,
                                                    removeDialogTitle ?? removeableTooltip,
                                                    RemoveDialogComponent !== undefined ? (
                                                        <RemoveDialogComponent dataItem={dataItem} />
                                                    ) : (
                                                        `Ar tikrai norite ${removeableTooltip.toLocaleLowerCase()} pasirinktą įrašą?`
                                                    ),
                                                    onItemRemove
                                                )
                                            }
                                            showRemoveButton={removeable !== false}
                                            removeableTooltip={removeableTooltip}
                                        />
                                    )}
                                </Tooltip>
                            )}
                        </td>
                    );
                }}
            />
        )
    };
};

Grid.displayName = 'KendoReactGrid';

const NewItemButtons: VFC<{ onSave: () => Promise<void>; onCancel: () => void }> = ({ onSave, onCancel }) => (
    <ButtonGroup>
        <Button type="button" fillMode="flat" themeColor={'primary'} iconClass="far fa-fw fa-check" title="Išsaugoti" onClick={onSave} />
        <Button type="button" fillMode="flat" className="k-error" iconClass="far fa-fw fa-minus" title="Atšaukti" onClick={onCancel} />
    </ButtonGroup>
);

const ItemInEditButtons: VFC<{ onSave: () => Promise<void>; onCancel: () => void }> = ({ onSave, onCancel }) => (
    <ButtonGroup>
        <Button type="button" fillMode="flat" themeColor={'primary'} iconClass="far fa-fw fa-save" title="Saugoti" onClick={onSave} />
        <Button type="button" fillMode="flat" className="k-error" iconClass="far fa-fw fa-times" title="Atšaukti" onClick={onCancel} />
    </ButtonGroup>
);

const EditButtons: VFC<{ onEdit: () => void; onRemove: () => Promise<void>; showRemoveButton: boolean; removeableTooltip: string }> = ({
    onEdit,
    onRemove,
    showRemoveButton,
    removeableTooltip
}) => (
    <ButtonGroup>
        <>
            <Button type="button" fillMode="flat" themeColor={'primary'} iconClass="far fa-fw fa-edit" title="Redaguoti" onClick={onEdit} />
            {showRemoveButton && (
                <Button
                    type="button"
                    fillMode="flat"
                    className="k-error"
                    iconClass="far fa-fw fa-trash"
                    title={removeableTooltip}
                    onClick={onRemove}
                />
            )}
        </>
    </ButtonGroup>
);

const useLoading = ({ isLoading, rowRender }: Props, data: any[] | DataResult) => {
    if (!isLoading) {
        return { data, rowRender };
    }

    return { data: { data: [{}], total: 0 }, rowRender: LoadingRowRender };
};

const LoadingRowRender: VFC = () => (
    <tr>
        <td colSpan={100}>
            <div className="d-flex justify-content-center align-items-center">
                <Loader type="infinite-spinner" />
            </div>
        </td>
    </tr>
);
